import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  listConsignments,
  recallShipment,
  gateInShipment,
  destuffManifest,
  destuffLine,
  uploadRecallDocs,
} from '../utils/gateoutApi';

/* ============================================================================
 *  Cold Store Management › Recall & De-stuff (tab module)
 *    Tab 1  Consignment gate-out  — executed FCLs; Recall -> On Transit
 *    Tab 2  Shipment recalled     — On Transit -> (Gate In) -> Reached Factory
 *    Tab 3  Recall & de-stuff     — Move to cold store -> de-stuff worklist
 *
 *  Status machine: EXECUTED -> ON_TRANSIT -> REACHED_FACTORY -> RETURNED
 *  Talks to api/gateout.php via ../utils/gateoutApi.
 * ========================================================================== */

const KG_TO_LB = 2.20;

const C = {
  bg: '#f8fafc', card: '#ffffff', border: '#e2e8f0', text: '#1e293b', muted: '#64748b',
  purple: '#7c3aed', purpleBg: '#f5f3ff', danger: '#dc2626', dangerBg: '#fef2f2',
  ok: '#15803d', okBg: '#f0fdf4', track: '#e2e8f0', amber: '#b45309', amberBg: '#fffbeb',
  blue: '#1d4ed8', blueBg: '#eff6ff',
};
const S = {
  wrap: { fontFamily: 'system-ui, -apple-system, sans-serif', color: C.text },
  title: { fontSize: 22, fontWeight: 600, margin: 0 },
  sub: { fontSize: 13.5, color: C.muted, margin: '6px 0 16px', maxWidth: 720, lineHeight: 1.55 },
  card: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 16 },
  fclRow: { display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center', justifyContent: 'space-between' },
  fact: { fontSize: 13, color: C.muted },
  factVal: { color: C.text, fontWeight: 600, fontSize: 14 },
  table: { width: '100%', borderCollapse: 'collapse', background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', display: 'block', overflowX: 'auto' },
  th: { textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.muted, padding: '10px 10px', borderBottom: `1px solid ${C.border}`, background: C.bg, textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap' },
  thFilter: { padding: '6px 8px', background: C.bg, borderBottom: `1px solid ${C.border}` },
  fin: { width: '100%', boxSizing: 'border-box', padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12.5, outline: 'none' },
  td: { padding: '10px 10px', fontSize: 13.5, borderBottom: `1px solid ${C.border}`, verticalAlign: 'middle', whiteSpace: 'nowrap' },
  btn: { padding: '8px 13px', border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 500, cursor: 'pointer' },
  ghost: { background: C.card, border: `1px solid ${C.border}`, color: C.text },
  primary: { background: C.purple, color: '#fff' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 },
  modal: { background: C.card, borderRadius: 14, width: '100%', maxWidth: 560, boxShadow: '0 20px 50px rgba(0,0,0,0.25)', maxHeight: '92vh', overflow: 'auto' },
  modalHead: { padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  modalBody: { padding: 20 },
  label: { display: 'block', fontSize: 12.5, fontWeight: 500, marginBottom: 5, marginTop: 12, color: C.text },
  inp: { width: '100%', boxSizing: 'border-box', padding: '8px 11px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14, outline: 'none' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  readout: { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginTop: 14, fontSize: 13 },
  rline: { display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: C.muted },
  rval: { color: C.text, fontWeight: 600 },
  alert: (k) => ({ fontSize: 13, padding: '10px 12px', borderRadius: 8, marginBottom: 12,
    background: k === 'error' ? C.dangerBg : C.okBg, color: k === 'error' ? C.danger : C.ok,
    border: `1px solid ${k === 'error' ? '#fecaca' : '#bbf7d0'}` }),
  tabs: { display: 'flex', gap: 4, borderBottom: `1px solid ${C.border}`, marginBottom: 20, flexWrap: 'wrap' },
  tab: (on) => ({ padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
    border: 'none', background: 'transparent', color: on ? C.purple : C.muted,
    borderBottom: `2px solid ${on ? C.purple : 'transparent'}`, marginBottom: -1 }),
};
const STATUS = {
  EXECUTED: { label: 'Executed', bg: '#eef2ff', fg: '#4338ca' },
  ON_TRANSIT: { label: 'On Transit', bg: C.amberBg, fg: C.amber },
  REACHED_FACTORY: { label: 'Reached Factory', bg: C.blueBg, fg: C.blue },
  RETURNED: { label: 'In cold store', bg: C.okBg, fg: C.ok },
};
const StatusPill = ({ s }) => {
  const v = STATUS[s] || { label: s, bg: '#eef2ff', fg: '#4338ca' };
  return <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: v.bg, color: v.fg }}>{v.label}</span>;
};
const fmtDT = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const nowLocal = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
const errMsg = (e) => e?.response?.data?.error || e?.message || 'Network error';

const FILTER_KEYS = [
  { k: 'container_no', label: 'Container No' }, { k: 'product', label: 'Product' },
  { k: 'brand', label: 'Brand' }, { k: 'grade', label: 'Grade' }, { k: 'pack_size', label: 'Pack Size' },
  { k: 'lot_no', label: 'Lot No' }, { k: 'trace_id', label: 'Trace ID' }, { k: 'day_code', label: 'Day Code' },
];
const LIST_COLS = [
  { k: 'fcl', label: 'FCL No', get: (r) => [r.gateout_no, r.fcl_no, r.fcl_month] },
  { k: 'containers', label: 'Container No', get: (r) => r.containers || [] },
  { k: 'buyer', label: 'Buyer / port', get: (r) => [r.buyer, r.destination_port] },
];

function useConsignments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const reload = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await listConsignments();
      if (r?.success) setRows(r.documents || []);
      else setErr(r?.error || 'Failed to load');
    }
    catch (e) { setErr(errMsg(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { rows, loading, err, reload };
}
function FilterRow({ filters, setFilters, extraCols }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <tr>
      {LIST_COLS.map((c) => (
        <th key={c.k} style={S.thFilter}>
          <input style={S.fin} placeholder={'Search ' + c.label.toLowerCase()} value={filters[c.k] || ''} onChange={(e) => set(c.k, e.target.value)} />
        </th>
      ))}
      {extraCols.map((_, i) => <th key={i} style={S.thFilter} />)}
      <th style={{ ...S.thFilter, textAlign: 'right' }}>
        <button style={{ ...S.btn, ...S.ghost, padding: '5px 10px', fontSize: 12 }} onClick={() => setFilters({})}>Clear</button>
      </th>
    </tr>
  );
}
const applyFilters = (rows, filters) => rows.filter((r) => LIST_COLS.every((c) => {
  const q = (filters[c.k] || '').trim().toLowerCase();
  return !q || c.get(r).filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
}));

/* ===== TAB 1 — Consignment gate-out ====================================== */
function GateOutTab({ onRecalled }) {
  const { rows, loading, err, reload } = useConsignments();
  const [filters, setFilters] = useState({});
  const [recallDoc, setRecallDoc] = useState(null);
  const [reason, setReason] = useState(''); const [recallDate, setRecallDate] = useState('');
  const [selCtr, setSelCtr] = useState([]); const [refDocs, setRefDocs] = useState([]);
  const [busy, setBusy] = useState(false); const [merr, setMerr] = useState('');

  const open = (r) => { setRecallDoc(r); setReason(''); setMerr(''); setRecallDate(new Date().toISOString().slice(0, 10)); setSelCtr(r.containers || []); setRefDocs([]); };
  const toggle = (c) => setSelCtr((s) => s.includes(c) ? s.filter((x) => x !== c) : [...s, c]);
  const apply = async () => {
    if (!reason.trim()) { setMerr('A recall reason is required'); return; }
    if (!recallDate) { setMerr('Recall date is required'); return; }
    if ((recallDoc.containers || []).length > 1 && selCtr.length === 0) { setMerr('Select at least one container'); return; }
    setBusy(true); setMerr('');
    try {
      let references = [];
      if (refDocs.length) {
        const up = await uploadRecallDocs(refDocs);
        if (!up?.success) { setMerr('Reference document upload failed'); setBusy(false); return; }
        references = up.files || [];
      }
      const res = await recallShipment({ doc_id: recallDoc.id, reason: reason.trim(), recall_date: recallDate, containers: selCtr, reference_docs: references });
      if (res?.success) { setRecallDoc(null); onRecalled(); } else setMerr(res?.error || 'Recall failed');
    } catch (e) { setMerr(errMsg(e)); } finally { setBusy(false); }
  };
  const list = applyFilters(rows.filter((r) => r.status === 'EXECUTED'), filters);

  return (
    <div>
      <h2 style={S.title}>Consignment gate-out</h2>
      <p style={S.sub}>Executed shipment consignments. Select an FCL and apply recall — it then moves to the Shipment recalled tab as On Transit.</p>
      {err ? <div style={S.alert('error')}>{err}</div> : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <button style={{ ...S.btn, ...S.ghost }} onClick={reload} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
      </div>
      <table style={S.table}>
        <thead>
          <tr>
            {LIST_COLS.map((c) => <th key={c.k} style={S.th}>{c.label}</th>)}
            <th style={S.th}>Gate-out date</th>
            <th style={{ ...S.th, textAlign: 'right' }}>Pallets / cartons</th>
            <th style={S.th}>Status</th>
            <th style={{ ...S.th, textAlign: 'right' }}>Action</th>
          </tr>
          <FilterRow filters={filters} setFilters={setFilters} extraCols={['Gate-out date', 'Pallets / cartons', 'Status']} />
        </thead>
        <tbody>
          {loading ? <tr><td style={S.td} colSpan={7}>Loading…</td></tr> : null}
          {!loading && list.length === 0 ? <tr><td style={{ ...S.td, color: C.muted }} colSpan={7}>No executed consignments.</td></tr> : null}
          {!loading && list.map((r) => (
            <tr key={r.id}>
              <td style={S.td}><div style={{ fontWeight: 600 }}>{r.gateout_no}</div><div style={{ fontSize: 12, color: C.muted }}>{r.fcl_no} · {r.fcl_month}</div></td>
              <td style={S.td}><div style={{ fontWeight: 600 }}>{r.container_count} container{r.container_count === 1 ? '' : 's'}</div><div style={{ fontSize: 12, color: C.muted, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{(r.containers || []).join(', ')}</div></td>
              <td style={S.td}><div>{r.buyer}</div><div style={{ fontSize: 12, color: C.muted }}>{r.destination_port}</div></td>
              <td style={S.td}>{r.gateout_date}</td>
              <td style={{ ...S.td, textAlign: 'right' }}><div style={{ fontWeight: 600 }}>{r.pallet_count} pallets</div><div style={{ fontSize: 12, color: C.muted }}>{r.total_cartons} ctns</div></td>
              <td style={S.td}><StatusPill s={r.status} /></td>
              <td style={{ ...S.td, textAlign: 'right' }}><button style={{ ...S.btn, ...S.primary }} onClick={() => open(r)}>Recall</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      {recallDoc ? (
        <div style={S.overlay} onClick={() => !busy && setRecallDoc(null)}>
          <div style={{ ...S.modal, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHead}><strong style={{ fontSize: 16 }}>Recall {recallDoc.gateout_no}</strong>
              <button onClick={() => !busy && setRecallDoc(null)} style={{ ...S.btn, background: 'transparent', color: C.muted, fontSize: 18, padding: '0 6px' }}>×</button></div>
            <div style={S.modalBody}>
              {merr ? <div style={S.alert('error')}>{merr}</div> : null}
              <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12 }}>
                <div style={S.rline}><span>Buyer / port</span><span style={S.rval}>{recallDoc.buyer} · {recallDoc.destination_port}</span></div>
                <div style={S.rline}><span>On this FCL</span><span style={S.rval}>{recallDoc.pallet_count} pallets · {recallDoc.total_cartons} cartons</span></div>
              </div>
              <label style={S.label}>Containers to recall <span style={{ color: C.danger }}>*</span></label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {(recallDoc.containers || []).map((c) => { const on = selCtr.includes(c); return (
                  <button key={c} onClick={() => toggle(c)} style={{ ...S.btn, padding: '6px 11px', fontSize: 13, border: `1px solid ${on ? C.purple : C.border}`, background: on ? C.purpleBg : C.card, color: on ? '#5b21b6' : C.text }}>{on ? '✓ ' : ''}{c}</button>
                ); })}
              </div>
              <div style={S.grid2}>
                <div><label style={S.label}>Recall date <span style={{ color: C.danger }}>*</span></label>
                  <input style={S.inp} type="date" value={recallDate} onChange={(e) => setRecallDate(e.target.value)} /></div>
                <div><label style={S.label}>Reference documents</label>
                  <input style={{ ...S.inp, padding: '6px 8px' }} type="file" multiple onChange={(e) => setRefDocs(Array.from(e.target.files || []))} /></div>
              </div>
              {refDocs.length ? <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>Attached: {refDocs.map((f) => f.name).join(', ')}</div> : null}
              <label style={S.label}>Recall reason <span style={{ color: C.danger }}>*</span></label>
              <input style={S.inp} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this container being recalled?" />
              <div style={{ ...S.readout, background: C.purpleBg, border: '1px solid #ddd6fe', color: '#5b21b6' }}>The selected container(s) are recalled whole and move to On Transit. Capture the factory gate-in next, then de-stuff into cold store.</div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
                <button style={{ ...S.btn, ...S.ghost }} onClick={() => setRecallDoc(null)} disabled={busy}>Cancel</button>
                <button style={{ ...S.btn, ...S.primary, opacity: busy ? 0.7 : 1 }} onClick={apply} disabled={busy}>{busy ? 'Recalling…' : 'Apply recall'}</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ===== TAB 2 — Shipment recalled ========================================= */
function RecalledTab({ onMoveToCS }) {
  const { rows, loading, err, reload } = useConsignments();
  const [filters, setFilters] = useState({});
  const [gateDoc, setGateDoc] = useState(null);
  const [gateInAt, setGateInAt] = useState('');
  const [busy, setBusy] = useState(false); const [merr, setMerr] = useState('');
  const [banner, setBanner] = useState('');

  const openGate = (r) => { setGateDoc(r); setGateInAt(nowLocal()); setMerr(''); };
  const confirmGate = async () => {
    setBusy(true); setMerr('');
    try {
      const res = await gateInShipment(gateDoc.id, gateInAt);
      if (res?.success) { setBanner(`${gateDoc.gateout_no} reached factory — gated in ${fmtDT(gateInAt)}`); setGateDoc(null); reload(); }
      else setMerr(res?.error || 'Gate-in failed');
    } catch (e) { setMerr(errMsg(e)); } finally { setBusy(false); }
  };
  const list = applyFilters(rows.filter((r) => r.status === 'ON_TRANSIT' || r.status === 'REACHED_FACTORY'), filters);

  return (
    <div>
      <h2 style={S.title}>Shipment recalled</h2>
      <p style={S.sub}>Recalled consignments on their way back. Capture the factory Gate-In (date &amp; time) to mark a container Reached factory, then move it to cold store to de-stuff.</p>
      {banner ? <div style={S.alert('ok')}>{banner}</div> : null}
      {err ? <div style={S.alert('error')}>{err}</div> : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <button style={{ ...S.btn, ...S.ghost }} onClick={reload} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
      </div>
      <table style={S.table}>
        <thead>
          <tr>
            {LIST_COLS.map((c) => <th key={c.k} style={S.th}>{c.label}</th>)}
            <th style={S.th}>Recall date</th>
            <th style={S.th}>Gate-in date &amp; time</th>
            <th style={S.th}>Status</th>
            <th style={{ ...S.th, textAlign: 'right' }}>Action</th>
          </tr>
          <FilterRow filters={filters} setFilters={setFilters} extraCols={['Recall date', 'Gate-in', 'Status']} />
        </thead>
        <tbody>
          {loading ? <tr><td style={S.td} colSpan={7}>Loading…</td></tr> : null}
          {!loading && list.length === 0 ? <tr><td style={{ ...S.td, color: C.muted }} colSpan={7}>No recalled shipments.</td></tr> : null}
          {!loading && list.map((r) => (
            <tr key={r.id}>
              <td style={S.td}><div style={{ fontWeight: 600 }}>{r.gateout_no}</div><div style={{ fontSize: 12, color: C.muted }}>{r.fcl_no} · {r.fcl_month}</div></td>
              <td style={S.td}><div style={{ fontWeight: 600 }}>{(r.recalled_containers || r.containers || []).join(', ')}</div></td>
              <td style={S.td}><div>{r.buyer}</div><div style={{ fontSize: 12, color: C.muted }}>{r.destination_port}</div></td>
              <td style={S.td}>{r.recall_date || '—'}</td>
              <td style={S.td}>{r.status === 'REACHED_FACTORY' ? fmtDT(r.gate_in_at) : '—'}</td>
              <td style={S.td}><StatusPill s={r.status} /></td>
              <td style={{ ...S.td, textAlign: 'right' }}>
                {r.status === 'ON_TRANSIT'
                  ? <button style={{ ...S.btn, ...S.primary }} onClick={() => openGate(r)}>Gate in</button>
                  : <button style={{ ...S.btn, ...S.ghost }} onClick={() => onMoveToCS(r.id)}>Move to cold store →</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {gateDoc ? (
        <div style={S.overlay} onClick={() => !busy && setGateDoc(null)}>
          <div style={{ ...S.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHead}><strong style={{ fontSize: 16 }}>Factory gate-in · {gateDoc.gateout_no}</strong>
              <button onClick={() => !busy && setGateDoc(null)} style={{ ...S.btn, background: 'transparent', color: C.muted, fontSize: 18, padding: '0 6px' }}>×</button></div>
            <div style={S.modalBody}>
              {merr ? <div style={S.alert('error')}>{merr}</div> : null}
              <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12 }}>
                <div style={S.rline}><span>Container(s)</span><span style={S.rval}>{(gateDoc.recalled_containers || []).join(', ')}</span></div>
                <div style={S.rline}><span>On recall</span><span style={S.rval}>{gateDoc.total_cartons} cartons</span></div>
              </div>
              <label style={S.label}>Gate-in date &amp; time <span style={{ color: C.danger }}>*</span></label>
              <input style={S.inp} type="datetime-local" value={gateInAt} onChange={(e) => setGateInAt(e.target.value)} />
              <div style={{ ...S.readout, background: C.blueBg, border: '1px solid #bfdbfe', color: C.blue }}>Recording the gate-in marks this container Reached factory. This date &amp; time stays fixed through de-stuff.</div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
                <button style={{ ...S.btn, ...S.ghost }} onClick={() => setGateDoc(null)} disabled={busy}>Cancel</button>
                <button style={{ ...S.btn, ...S.primary, opacity: busy ? 0.7 : 1 }} onClick={confirmGate} disabled={busy || !gateInAt}>{busy ? 'Saving…' : 'Confirm gate-in'}</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ===== TAB 3 — Recall & de-stuff ========================================= */
function DestuffTab({ docId, setDocId }) {
  if (docId) return <DestuffWorklist docId={docId} onBack={() => setDocId(null)} />;
  return <DestuffPicker onOpen={setDocId} />;
}
function DestuffPicker({ onOpen }) {
  const { rows, loading, err, reload } = useConsignments();
  const list = rows.filter((r) => r.status === 'REACHED_FACTORY');
  return (
    <div>
      <h2 style={S.title}>Recall &amp; de-stuff</h2>
      <p style={S.sub}>Containers that reached the factory and are ready to de-stuff into cold store. Pick one to open its worklist.</p>
      {err ? <div style={S.alert('error')}>{err}</div> : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <button style={{ ...S.btn, ...S.ghost }} onClick={reload} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
      </div>
      <table style={S.table}>
        <thead><tr>
          <th style={S.th}>FCL No</th><th style={S.th}>Container No</th><th style={S.th}>Buyer / port</th>
          <th style={S.th}>Gate-in date &amp; time</th><th style={{ ...S.th, textAlign: 'right' }}>Remaining</th><th style={{ ...S.th, textAlign: 'right' }}>Action</th>
        </tr></thead>
        <tbody>
          {loading ? <tr><td style={S.td} colSpan={6}>Loading…</td></tr> : null}
          {!loading && list.length === 0 ? <tr><td style={{ ...S.td, color: C.muted }} colSpan={6}>Nothing ready to de-stuff. Gate in a recalled shipment first.</td></tr> : null}
          {!loading && list.map((r) => (
            <tr key={r.id}>
              <td style={S.td}><div style={{ fontWeight: 600 }}>{r.gateout_no}</div><div style={{ fontSize: 12, color: C.muted }}>{r.fcl_no}</div></td>
              <td style={S.td}>{(r.recalled_containers || []).join(', ')}</td>
              <td style={S.td}><div>{r.buyer}</div><div style={{ fontSize: 12, color: C.muted }}>{r.destination_port}</div></td>
              <td style={S.td}>{fmtDT(r.gate_in_at)}</td>
              <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}>{r.remaining_cartons}/{r.total_cartons}</td>
              <td style={{ ...S.td, textAlign: 'right' }}><button style={{ ...S.btn, ...S.primary }} onClick={() => onOpen(r.id)}>Open de-stuff</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DestuffWorklist({ docId, onBack }) {
  const [doc, setDoc] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageErr, setPageErr] = useState(''); const [banner, setBanner] = useState('');
  const [filters, setFilters] = useState({});
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setPageErr('');
    try {
      const res = await destuffManifest(docId);
      if (res?.success) { setDoc(res.document); setLines(res.lines || []); }
      else setPageErr(res?.error || 'Failed to load');
    }
    catch (e) { setPageErr(errMsg(e)); } finally { setLoading(false); }
  }, [docId]);
  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const filtered = useMemo(() => lines.filter((l) => FILTER_KEYS.every(({ k }) => {
    const q = (filters[k] || '').trim().toLowerCase(); return !q || String(l[k] || '').toLowerCase().includes(q);
  })), [lines, filters]);
  const remainingTotal = lines.reduce((s, l) => s + l.remaining_cartons, 0);
  const shippedTotal = lines.reduce((s, l) => s + l.shipped_cartons, 0);
  const pct = shippedTotal ? Math.round(((shippedTotal - remainingTotal) / shippedTotal) * 100) : 0;
  const fclDone = remainingTotal === 0;

  const onDestuffed = (res, cartons) => {
    setLines((prev) => prev.map((l) => l.line_id === active.line_id ? { ...l, remaining_cartons: l.remaining_cartons - cartons } : l));
    if (res.fully_destuffed && doc) setDoc({ ...doc, status: 'RETURNED' });
    setBanner(res.message || 'De-stuffed.'); setActive(null);
  };

  return (
    <div>
      <button style={{ ...S.btn, ...S.ghost, marginBottom: 12 }} onClick={onBack}>← Back to list</button>
      <h2 style={S.title}>Recall &amp; de-stuff</h2>
      <p style={S.sub}>Unload the recalled container into cold store. Filter the manifest, then build plates by product + brand + grade + pack size. Each entry draws the remaining cartons down; when the container is empty it is marked In cold store.</p>
      {banner ? <div style={S.alert('ok')}>{banner}</div> : null}
      {pageErr ? <div style={S.alert('error')}>{pageErr}</div> : null}

      {doc ? (
        <div style={S.card}>
          <div style={S.fclRow}>
            <div><div style={S.fact}>FCL / gate-out</div><div style={S.factVal}>{doc.gateout_no} · {doc.fcl_no}</div></div>
            <div><div style={S.fact}>Gate-in date &amp; time</div><div style={S.factVal}>{fmtDT(doc.gate_in_at)}</div></div>
            <div><div style={S.fact}>Status</div><div><StatusPill s={doc.status} /></div></div>
            <div style={{ minWidth: 220, flex: 1 }}>
              <div style={S.fact}>De-stuff progress · {shippedTotal - remainingTotal}/{shippedTotal} cartons</div>
              <div style={{ height: 8, background: C.track, borderRadius: 999, marginTop: 6, overflow: 'hidden' }}>
                <div style={{ width: pct + '%', height: '100%', background: fclDone ? C.ok : C.purple }} /></div>
            </div>
          </div>
        </div>
      ) : null}

      <table style={S.table}>
        <thead>
          <tr>
            {FILTER_KEYS.map(({ k, label }) => <th key={k} style={S.th}>{label}</th>)}
            <th style={{ ...S.th, textAlign: 'right' }}>Shipped Qty</th>
            <th style={{ ...S.th, textAlign: 'right' }}>Remaining</th>
            <th style={{ ...S.th, textAlign: 'right' }}>Action</th>
          </tr>
          <tr>
            {FILTER_KEYS.map(({ k, label }) => <th key={k} style={S.thFilter}><input style={S.fin} placeholder={'Search ' + label.toLowerCase()} value={filters[k] || ''} onChange={(e) => setFilter(k, e.target.value)} /></th>)}
            <th style={S.thFilter} /><th style={S.thFilter} />
            <th style={{ ...S.thFilter, textAlign: 'right' }}><button style={{ ...S.btn, ...S.ghost, padding: '5px 10px', fontSize: 12 }} onClick={() => setFilters({})}>Clear</button></th>
          </tr>
        </thead>
        <tbody>
          {loading ? <tr><td style={S.td} colSpan={11}>Loading manifest…</td></tr> : null}
          {!loading && filtered.length === 0 ? <tr><td style={{ ...S.td, color: C.muted }} colSpan={11}>No matching lines.</td></tr> : null}
          {!loading && filtered.map((l) => {
            const done = l.remaining_cartons === 0;
            return (
              <tr key={l.line_id} style={done ? { opacity: 0.5 } : null}>
                <td style={{ ...S.td, fontWeight: 600 }}>{l.container_no}</td>
                <td style={S.td}>{l.product}</td><td style={S.td}>{l.brand}</td><td style={S.td}>{l.grade}</td>
                <td style={S.td}>{l.pack_size}</td><td style={S.td}>{l.lot_no}</td><td style={S.td}>{l.trace_id}</td><td style={S.td}>{l.day_code}</td>
                <td style={{ ...S.td, textAlign: 'right' }}>{l.shipped_cartons}</td>
                <td style={{ ...S.td, textAlign: 'right', fontWeight: 600, color: done ? C.ok : C.text }}>{l.remaining_cartons}</td>
                <td style={{ ...S.td, textAlign: 'right' }}>
                  <button style={{ ...S.btn, ...S.primary, opacity: done ? 0.4 : 1, cursor: done ? 'default' : 'pointer' }} disabled={done} onClick={() => setActive(l)}>De-stuff</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {fclDone && doc ? <div style={{ ...S.alert('ok'), marginTop: 16, fontSize: 14 }}>FCL {doc.gateout_no} fully de-stuffed — all new pallets are PRODUCED stock in cold store.</div> : null}

      {active ? <DestuffModal docId={doc.id} gateInAt={doc.gate_in_at} line={active} onClose={() => setActive(null)} onDone={onDestuffed} /> : null}
    </div>
  );
}

const blankPlate = () => ({ pallet_id: '', cartons: '', bin: '' });
function DestuffModal({ docId, gateInAt, line, onClose, onDone }) {
  const [csSel, setCsSel] = useState('CS1');
  const [wtCartonKg, setWtCartonKg] = useState(String(line.wt_per_carton_kg || ''));
  const [plates, setPlates] = useState([blankPlate()]);
  const [submitting, setSubmitting] = useState(false); const [err, setErr] = useState('');

  const nWt = parseFloat(wtCartonKg) || 0;
  const setPlate = (i, k, v) => setPlates((p) => p.map((pl, idx) => idx === i ? { ...pl, [k]: v } : pl));
  const addPlate = () => setPlates((p) => [...p, blankPlate()]);
  const removePlate = (i) => setPlates((p) => p.length > 1 ? p.filter((_, idx) => idx !== i) : p);
  const plateKg = (pl) => +((parseInt(pl.cartons, 10) || 0) * nWt).toFixed(2);
  const totalCartons = plates.reduce((s, pl) => s + (parseInt(pl.cartons, 10) || 0), 0);
  const grandKg = +plates.reduce((s, pl) => s + plateKg(pl), 0).toFixed(2);
  const overCap = totalCartons > line.remaining_cartons;

  const submit = async () => {
    const clean = plates.map((pl) => ({ pallet_id: pl.pallet_id.trim(), bin: pl.bin.trim(), cartons: parseInt(pl.cartons, 10) || 0 }));
    if (clean.some((pl) => !pl.pallet_id)) { setErr('Every plate needs a pallet ID'); return; }
    if (clean.some((pl) => pl.cartons <= 0)) { setErr('Every plate needs cartons greater than 0'); return; }
    if (totalCartons > line.remaining_cartons) { setErr(`Plates total ${totalCartons} cartons but only ${line.remaining_cartons} remain`); return; }
    setSubmitting(true); setErr('');
    try {
      const res = await destuffLine({ doc_id: docId, source_line_id: line.line_id, gate_in_at: gateInAt, cs_selection: csSel, wt_per_carton_kg: nWt, plates: clean });
      if (res?.success) onDone(res, res.cartons_destuffed || totalCartons); else setErr(res?.error || 'De-stuff failed');
    } catch (e) { setErr(errMsg(e)); } finally { setSubmitting(false); }
  };

  return (
    <div style={S.overlay} onClick={() => !submitting && onClose()}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.modalHead}><strong style={{ fontSize: 16 }}>De-stuff &amp; cold store gate-in</strong>
          <button onClick={() => !submitting && onClose()} style={{ ...S.btn, background: 'transparent', color: C.muted, fontSize: 18, padding: '0 6px' }}>×</button></div>
        <div style={S.modalBody}>
          {err ? <div style={S.alert('error')}>{err}</div> : null}
          <div style={{ background: C.purpleBg, border: '1px solid #ddd6fe', borderRadius: 10, padding: 12, fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: '#5b21b6' }}>{line.product} · {line.brand} · {line.grade} · {line.pack_size}</div>
            <div style={{ color: '#6d28d9', marginTop: 3 }}>Container {line.container_no} · Lot {line.lot_no} · Trace {line.trace_id} · Day {line.day_code} · {line.remaining_cartons} cartons remaining</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 12 }}>
            <div><label style={S.label}>CS gate-in date &amp; time</label>
              <input style={{ ...S.inp, background: C.bg, color: C.muted }} type="datetime-local" value={gateInAt || ''} readOnly /></div>
            <div><label style={S.label}>Cold store</label>
              <select style={S.inp} value={csSel} onChange={(e) => setCsSel(e.target.value)}><option value="CS1">CS1</option><option value="CS2">CS2</option></select></div>
            <div><label style={S.label}>Wt / carton (kg) · {(nWt * KG_TO_LB).toFixed(2)} lb</label>
              <input style={S.inp} type="number" step="0.01" value={wtCartonKg} onChange={(e) => setWtCartonKg(e.target.value)} /></div>
          </div>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ fontSize: 13.5 }}>Plates ({plates.length})</strong>
            <span style={{ fontSize: 12.5, color: overCap ? C.danger : C.muted }}>{totalCartons} / {line.remaining_cartons} cartons · {grandKg.toFixed(2)} kg</span>
          </div>
          {plates.map((pl, i) => (
            <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>PLATE {i + 1}</span>
                {plates.length > 1 ? <button onClick={() => removePlate(i)} style={{ ...S.btn, background: 'transparent', color: C.danger, padding: '2px 6px', fontSize: 12 }}>Remove</button> : null}
              </div>
              <div style={S.grid2}>
                <div><label style={S.label}>Pallet ID <span style={{ color: C.danger }}>*</span></label>
                  <input style={S.inp} value={pl.pallet_id} onChange={(e) => setPlate(i, 'pallet_id', e.target.value)} placeholder="e.g. P-50231" /></div>
                <div><label style={S.label}>Cartons <span style={{ color: C.danger }}>*</span></label>
                  <input style={S.inp} type="number" min="0" value={pl.cartons} onChange={(e) => setPlate(i, 'cartons', e.target.value)} /></div>
              </div>
              <div><label style={S.label}>{csSel} bin</label>
                <input style={S.inp} value={pl.bin} onChange={(e) => setPlate(i, 'bin', e.target.value)} placeholder="e.g. 1A2-3" /></div>
              <div style={{ ...S.rline, marginTop: 8 }}><span style={{ fontSize: 12.5, color: C.muted }}>Plate total</span>
                <span style={{ fontWeight: 600, color: C.purple }}>{plateKg(pl).toFixed(2)} kg / {(plateKg(pl) * KG_TO_LB).toFixed(2)} lb</span></div>
            </div>
          ))}
          <button style={{ ...S.btn, ...S.ghost, marginTop: 10, width: '100%' }} onClick={addPlate}>+ Add another plate</button>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
            <button style={{ ...S.btn, ...S.ghost }} onClick={onClose} disabled={submitting}>Cancel</button>
            <button style={{ ...S.btn, ...S.primary, opacity: (submitting || overCap) ? 0.6 : 1 }} onClick={submit} disabled={submitting || overCap}>{submitting ? 'Gating in…' : `De-stuff & gate-in (${totalCartons})`}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===== Default export: the tab shell ===================================== */
export default function RecallDestuff() {
  const [tab, setTab] = useState('gateout');
  const [destuffId, setDestuffId] = useState(null);
  const TABS = [
    { k: 'gateout', label: 'Consignment gate-out' },
    { k: 'recalled', label: 'Shipment recalled' },
    { k: 'destuff', label: 'Recall & de-stuff' },
  ];
  return (
    <div style={S.wrap}>
      <div style={S.tabs}>
        {TABS.map((t) => (
          <button key={t.k} style={S.tab(tab === t.k)} onClick={() => setTab(t.k)}>{t.label}</button>
        ))}
      </div>
      {tab === 'gateout' ? <GateOutTab onRecalled={() => setTab('recalled')} /> : null}
      {tab === 'recalled' ? <RecalledTab onMoveToCS={(id) => { setDestuffId(id); setTab('destuff'); }} /> : null}
      {tab === 'destuff' ? <DestuffTab docId={destuffId} setDocId={setDestuffId} /> : null}
    </div>
  );
}
